import React, { useState, useCallback, useEffect } from 'react';
import { AlertTriangle, Loader2, Save, X, FileText } from 'lucide-react';
import api from '../../../services/api';
import LightningIcon from './icons/LightningIcon';
import ClockIcon from './icons/ClockIcon';

const CATEGORY_LABELS = {
    'droitAerien': { fr: 'Droit Aérien', en: 'Air Law' },
    'meteo': { fr: 'Météo', en: 'Meteo' },
    'navigation': { fr: 'Navigation', en: 'Navigation' },
    'connaissance': { fr: 'Conn. Générale', en: 'Gen. Knowledge' },
    'complet': { fr: 'Examen complet', en: 'Full Exam' },
    'keywords': { fr: 'Mots-clés', en: 'Keywords' }
};

const FILTER_TYPES = [
    { id: 'level', label: { fr: 'Tout le niveau', en: 'Full level' } },
    { id: 'category', label: { fr: 'Par catégorie', en: 'By category' } },
    { id: 'exam', label: { fr: 'Par examen', en: 'By exam' } },
];

const ERROR_MODES = [
    { id: 'never', label: { fr: 'Jamais réussies', en: 'Never correct' }, desc: { fr: 'Jamais trouvé la bonne réponse', en: 'Never answered correctly' } },
    { id: 'sometimes', label: { fr: 'Erreurs ponctuelles', en: 'Occasional errors' }, desc: { fr: 'Parfois juste, parfois faux', en: 'Sometimes right, sometimes wrong' } },
    { id: 'recent', label: { fr: 'Erreurs récentes', en: 'Recent errors' }, desc: { fr: 'Ratées lors des 3 dernières sessions', en: 'Wrong in the last 3 sessions' } },
];

const ErrorExamSection = ({ levelId, language, onStartExam, activeExams, requiresUpgrade = false, onUpgrade }) => {
    const [errorMode, setErrorMode] = useState('never'); // 'never' | 'sometimes'
    const [filterType, setFilterType] = useState('level'); // 'level' | 'category' | 'exam'
    const [filterValue, setFilterValue] = useState([]);
    const [searching, setSearching] = useState(false);
    const [loadingPageIdx, setLoadingPageIdx] = useState(null);
    const [searchResult, setSearchResult] = useState(null);
    const [error, setError] = useState(null);
    const [showModeModal, setShowModeModal] = useState(null);
    const [presets, setPresets] = useState([]);
    const [presetName, setPresetName] = useState('');
    const [showSavePreset, setShowSavePreset] = useState(false);
    const [savingPreset, setSavingPreset] = useState(false);
    const [loadingAnnexe, setLoadingAnnexe] = useState(false);

    // Load presets from DB
    useEffect(() => {
        api.getCustomExamPresets(levelId, 'error').then(setPresets).catch(() => {});
    }, [levelId]);

    const handleSavePreset = useCallback(async () => {
        const name = presetName.trim();
        if (!name) return;
        setSavingPreset(true);
        try {
            await api.saveCustomExamPreset({
                levelId,
                presetType: 'error',
                name,
                config: { errorMode, filterType, filterValue },
                resultCount: searchResult?.totalQuestions ?? 0
            });
            setPresetName('');
            setShowSavePreset(false);
            const updated = await api.getCustomExamPresets(levelId, 'error');
            setPresets(updated);
        } catch { /* ignore */ }
        setSavingPreset(false);
    }, [presetName, levelId, errorMode, filterType, filterValue, searchResult]);

    const handleDeletePreset = useCallback(async (presetId) => {
        try {
            await api.deleteCustomExamPreset(presetId);
            setPresets(prev => prev.filter(p => p.id !== presetId));
        } catch { /* ignore */ }
    }, []);

    const handleLoadPreset = useCallback((preset) => {
        const config = preset.config;
        setErrorMode(config.errorMode || 'never');
        setFilterType(config.filterType || 'level');
        // Backward compat: old presets store single value, new ones store array
        const fv = config.filterValue;
        setFilterValue(Array.isArray(fv) ? fv : (fv ? [fv] : []));
        setSearchResult(null);
    }, []);

    const handleAnnexeDownload = useCallback(async () => {
        if (!searchResult?.examId || loadingAnnexe) return;
        setLoadingAnnexe(true);
        try {
            await api.downloadAnnexeBlob(searchResult.examId, `annexe_${searchResult.examId}.pdf`);
        } catch (e) {
            console.error('Annexe download failed:', e);
        } finally {
            setLoadingAnnexe(false);
        }
    }, [searchResult?.examId, loadingAnnexe]);

    // Build unique categories from active exams
    const availableCategories = (() => {
        const cats = new Set();
        (activeExams || []).forEach(e => {
            if (e.category && e.category !== 'keywords') cats.add(e.category);
        });
        return Array.from(cats).map(c => ({
            id: c,
            label: CATEGORY_LABELS[c] || { fr: c, en: c }
        }));
    })();

    // Build exam list for selector (exclude keywords AND exams requiring upgrade)
    const availableExams = (activeExams || []).filter(e => e.category !== 'keywords' && !e.requiresUpgrade);

    const getLocalizedText = (textObj) => {
        if (!textObj) return '';
        if (typeof textObj === 'string') return textObj;
        return textObj[language] || textObj['en'] || '';
    };

    const handleSearch = useCallback(async () => {
        setSearching(true);
        setError(null);
        setSearchResult(null);

        try {
            const result = await api.searchErrorQuestions(
                levelId,
                filterType,
                filterType !== 'level' && filterValue.length > 0 ? filterValue : null,
                errorMode
            );
            setSearchResult(result);
        } catch (err) {
            setError(language === 'fr'
                ? 'Erreur lors de la recherche. Veuillez réessayer.'
                : 'Search error. Please try again.');
        } finally {
            setSearching(false);
        }
    }, [levelId, filterType, filterValue, errorMode, language]);

    const handleStartExam = useCallback((pageIdx) => {
        const page = searchResult?.pages?.[pageIdx];
        if (!page) return;
        setShowModeModal({ pageIdx, questionIds: page.questionIds });
    }, [searchResult]);

    const handleModeSelected = useCallback(async (mode) => {
        if (!showModeModal) return;
        const { pageIdx, questionIds } = showModeModal;
        setShowModeModal(null);
        setLoadingPageIdx(pageIdx);

        try {
            const questions = await api.getQuestionsByIds(questionIds);
            if (questions.length === 0) {
                setError(language === 'fr'
                    ? 'Aucune question trouvée.'
                    : 'No questions found.');
                return;
            }
            onStartExam(questions, mode, {
                examType: 'error',
                config: { errorMode, filterType, filterValue },
                pageIndex: pageIdx,
                annexeExamId: searchResult?.hasAnnexe ? searchResult.examId : null
            });
        } catch (err) {
            setError(language === 'fr'
                ? 'Erreur lors du chargement des questions.'
                : 'Error loading questions.');
        } finally {
            setLoadingPageIdx(null);
        }
    }, [showModeModal, language, onStartExam, errorMode, filterType, filterValue, searchResult]);

    // Filter label for results
    const getFilterLabel = () => {
        if (filterType === 'category' && filterValue.length > 0) {
            return filterValue.map(v => {
                const cat = CATEGORY_LABELS[v];
                return cat ? getLocalizedText(cat) : v;
            }).join(', ');
        }
        if (filterType === 'exam' && filterValue.length > 0) {
            return filterValue.map(v => {
                const exam = availableExams.find(e => String(e.id) === String(v));
                return exam ? getLocalizedText(exam.title) : `#${v}`;
            }).join(', ');
        }
        return language === 'fr' ? 'Tout le niveau' : 'Full level';
    };

    return (
        <div data-help="custom-errors" className="space-y-4">
            <h2 className="text-lg font-black text-gray-400 dark:text-white ml-2 text-xs uppercase tracking-widest opacity-60 dark:opacity-40">
                {language === 'fr' ? 'Travailler mes erreurs' : 'Work on my mistakes'}
            </h2>

            {/* Filter Card */}
            <div className="bg-white dark:bg-[#121212] rounded-2xl p-5 border border-gray-200 dark:border-white/5 shadow-sm dark:shadow-none">
                <div className="flex flex-col gap-4">
                    {/* Error mode toggle */}
                    <div className="flex gap-2">
                        {ERROR_MODES.map(em => (
                            <button
                                key={em.id}
                                onClick={() => { setErrorMode(em.id); setSearchResult(null); }}
                                className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-bold transition-all text-left ${
                                    errorMode === em.id
                                        ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-2 border-orange-400 dark:border-orange-600'
                                        : 'bg-gray-50 dark:bg-white/5 text-gray-500 dark:text-gray-400 border-2 border-transparent hover:bg-gray-100 dark:hover:bg-white/10'
                                }`}
                            >
                                <div>{getLocalizedText(em.label)}</div>
                                <div className={`text-xs font-normal mt-0.5 ${errorMode === em.id ? 'text-orange-600/70 dark:text-orange-400/60' : 'text-gray-400 dark:text-gray-500'}`}>
                                    {getLocalizedText(em.desc)}
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Scope label */}
                    <div>
                        <p className="text-xs uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">
                            {language === 'fr' ? 'Périmètre' : 'Scope'}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {filterType === 'exam' && filterValue.length === 1
                                ? (language === 'fr'
                                    ? 'Examen unique : les questions utilisant les annexes seront incluses. Découpage en examens de 50 questions maximum.'
                                    : 'Single exam: questions using appendices will be included. Split into exams of 50 questions max.')
                                : (language === 'fr'
                                    ? 'Les questions utilisant les annexes ne seront pas incluses. Découpage en examens de 50 questions maximum.'
                                    : 'Questions using appendices will not be included. Split into exams of 50 questions max.')}
                        </p>
                    </div>

                    {/* Filter type toggle buttons */}
                    <div className="flex gap-2">
                        {FILTER_TYPES.map(ft => (
                            <button
                                key={ft.id}
                                onClick={() => {
                                    setFilterType(ft.id);
                                    setFilterValue([]);
                                    setSearchResult(null);
                                }}
                                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                                    filterType === ft.id
                                        ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                                        : 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10'
                                }`}
                            >
                                {getLocalizedText(ft.label)}
                            </button>
                        ))}
                    </div>

                    {/* Category chips (multi-select) */}
                    {filterType === 'category' && availableCategories.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {availableCategories.map(cat => {
                                const selected = filterValue.includes(cat.id);
                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => {
                                            setFilterValue(prev => selected ? prev.filter(v => v !== cat.id) : [...prev, cat.id]);
                                            setSearchResult(null);
                                        }}
                                        className={`px-3.5 py-2 rounded-xl text-sm font-semibold transition-all ${
                                            selected
                                                ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-300 dark:border-orange-700/50 shadow-sm'
                                                : 'bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10'
                                        }`}
                                    >
                                        {getLocalizedText(cat.label)}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Exam chips (multi-select) */}
                    {filterType === 'exam' && availableExams.length > 0 && (
                        <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto custom-scrollbar">
                            {availableExams.map(exam => {
                                const examIdStr = String(exam.id);
                                const selected = filterValue.includes(examIdStr);
                                return (
                                    <button
                                        key={exam.id}
                                        onClick={() => {
                                            setFilterValue(prev => selected ? prev.filter(v => v !== examIdStr) : [...prev, examIdStr]);
                                            setSearchResult(null);
                                        }}
                                        className={`px-3.5 py-2 rounded-xl text-sm font-semibold transition-all ${
                                            selected
                                                ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-300 dark:border-orange-700/50 shadow-sm'
                                                : 'bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10'
                                        }`}
                                    >
                                        {getLocalizedText(exam.title)}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Generate / Unlock button */}
                    {requiresUpgrade ? (
                        <button
                            onClick={onUpgrade}
                            className="self-end px-5 py-2.5 rounded-xl font-bold text-sm text-white shadow-lg transition bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                            {language === 'fr' ? 'Débloquer' : 'Unlock'}
                        </button>
                    ) : (
                        <button
                            onClick={handleSearch}
                            disabled={searching || (filterType !== 'level' && filterValue.length === 0)}
                            className="self-end px-5 py-2.5 rounded-xl font-bold text-sm text-white shadow-lg transition bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {searching ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <AlertTriangle className="w-4 h-4" />
                            )}
                            {language === 'fr' ? 'Générer' : 'Generate'}
                        </button>
                    )}
                    {/* Saved presets */}
                    {presets.length > 0 && (
                        <div className="pt-2 border-t border-gray-100 dark:border-white/5">
                            <p className="text-xs uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-2">
                                {language === 'fr' ? 'Configurations sauvegardées' : 'Saved configurations'}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {presets.map((preset) => (
                                    <div key={preset.id} className="inline-flex items-center gap-1">
                                        <button
                                            onClick={() => handleLoadPreset(preset)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-sm font-medium text-gray-700 dark:text-gray-300 transition"
                                        >
                                            {preset.name}
                                            <span className="text-gray-400 dark:text-gray-500">({preset.lastResultCount}q)</span>
                                        </button>
                                        <button
                                            onClick={() => handleDeletePreset(preset.id)}
                                            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-white/10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Save preset (after results) */}
                    {searchResult && searchResult.totalQuestions > 0 && (
                        <div className="pt-2 border-t border-gray-100 dark:border-white/5">
                            {showSavePreset ? (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={presetName}
                                        onChange={(e) => setPresetName(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                                        placeholder={language === 'fr' ? 'Nom de la configuration...' : 'Configuration name...'}
                                        className="flex-1 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm text-gray-900 dark:text-white placeholder-gray-400 outline-none focus:border-orange-400"
                                        autoFocus
                                    />
                                    <button
                                        onClick={handleSavePreset}
                                        disabled={!presetName.trim() || savingPreset}
                                        className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-bold disabled:opacity-40 hover:bg-orange-600 transition"
                                    >
                                        {savingPreset ? <Loader2 className="w-3 h-3 animate-spin" /> : (language === 'fr' ? 'Enregistrer' : 'Save')}
                                    </button>
                                    <button
                                        onClick={() => setShowSavePreset(false)}
                                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400 transition"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => { setShowSavePreset(true); setPresetName(''); }}
                                    className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-orange-500 dark:hover:text-orange-400 transition"
                                >
                                    <Save className="w-3.5 h-3.5" />
                                    {language === 'fr' ? 'Sauvegarder cette configuration' : 'Save this configuration'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-xl p-3 text-sm text-red-600 dark:text-red-400">
                    {error}
                </div>
            )}

            {/* Results */}
            {searchResult && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                            <span className="font-bold text-gray-900 dark:text-white">{searchResult.totalQuestions}</span>
                            {' '}{language === 'fr' ? 'erreurs trouvées' : 'errors found'}
                            {searchResult.totalPages > 1 && (
                                <span className="text-gray-400"> — {searchResult.totalPages} {language === 'fr' ? 'examens' : 'exams'}</span>
                            )}
                        </p>
                        <div className="flex items-center gap-2">
                            {searchResult.hasAnnexe && (
                                <button
                                    onClick={handleAnnexeDownload}
                                    disabled={loadingAnnexe}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-900/30 transition ${loadingAnnexe ? 'opacity-60 cursor-wait' : ''}`}
                                    title={language === 'fr' ? 'Télécharger l\'annexe PDF' : 'Download PDF appendix'}
                                >
                                    {loadingAnnexe ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                        <FileText className="w-3.5 h-3.5" />
                                    )}
                                    {language === 'fr' ? 'Annexe' : 'Appendix'}
                                </button>
                            )}
                            <button
                                onClick={() => setSearchResult(null)}
                                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                            >
                                {language === 'fr' ? 'Effacer' : 'Clear'}
                            </button>
                        </div>
                    </div>

                    {searchResult.totalQuestions === 0 ? (
                        <div className="bg-white dark:bg-[#121212] rounded-2xl p-6 border border-gray-200 dark:border-white/5 text-center">
                            <p className="text-gray-500 dark:text-gray-400 text-sm">
                                {language === 'fr'
                                    ? 'Aucune erreur trouvée pour ce filtre. Bravo !'
                                    : 'No errors found for this filter. Well done!'}
                            </p>
                        </div>
                    ) : (
                        searchResult.pages.map((page, idx) => (
                            <div
                                key={idx}
                                className="bg-white dark:bg-[#121212] rounded-2xl p-4 md:p-5 border border-gray-200 dark:border-white/5 flex items-center justify-between group hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-all shadow-sm dark:shadow-none"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                                        {searchResult.totalPages > 1 ? `${idx + 1}/${searchResult.totalPages}` : (
                                            <AlertTriangle className="w-5 h-5" />
                                        )}
                                    </div>
                                    <div>
                                        <h4 className="text-sm md:text-base font-bold text-gray-900 dark:text-white">
                                            {language === 'fr' ? 'Erreurs' : 'Errors'} — {getFilterLabel()}
                                            {searchResult.totalPages > 1 && (
                                                <span className="text-gray-400 font-normal ml-2">({idx + 1}/{searchResult.totalPages})</span>
                                            )}
                                        </h4>
                                        <p className="text-sm text-gray-500">
                                            {page.count} {language === 'fr' ? 'questions' : 'questions'}
                                            {' · '}{language === 'fr' ? 'Non comptabilisé' : 'Not scored'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleStartExam(idx)}
                                    disabled={loadingPageIdx === idx}
                                    className="px-5 py-2 rounded-xl font-bold text-sm text-white shadow-lg transition bg-orange-500 hover:bg-orange-600 disabled:opacity-60 flex items-center gap-2"
                                >
                                    {loadingPageIdx === idx ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : null}
                                    {language === 'fr' ? 'Commencer' : 'Start'}
                                </button>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Mode Selection Modal */}
            {showModeModal && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-800 animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {language === 'fr' ? 'Choisir le mode' : 'Exam mode selection'}
                            </h3>
                            <button onClick={() => setShowModeModal(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition">
                                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={() => handleModeSelected('flash')}
                                className="w-full p-6 bg-emerald-500/10 hover:bg-emerald-500/20 border-2 border-emerald-500/20 hover:border-emerald-500 rounded-2xl transition text-left group"
                            >
                                <div className="flex items-center gap-4 mb-2">
                                    <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white">
                                        <LightningIcon className="w-6 h-6" />
                                    </div>
                                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                                        {language === 'fr' ? 'Mode question-réponse' : 'Flashcard Mode'}
                                    </span>
                                </div>
                                <p className="text-gray-500 dark:text-gray-400 text-sm">
                                    {language === 'fr'
                                        ? 'Obtenez la bonne réponse et l\'explication à la validation de chaque question.'
                                        : 'Receive the correct answer and explanation after validating each question.'}
                                </p>
                            </button>

                            <button
                                onClick={() => handleModeSelected('classic')}
                                className="w-full p-6 bg-blue-500/10 hover:bg-blue-500/20 border-2 border-blue-500/20 hover:border-blue-500 rounded-2xl transition text-left group"
                            >
                                <div className="flex items-center gap-4 mb-2">
                                    <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center text-white">
                                        <ClockIcon className="w-6 h-6" />
                                    </div>
                                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                                        {language === 'fr' ? 'Mode examen' : 'Exam Mode'}
                                    </span>
                                </div>
                                <p className="text-gray-500 dark:text-gray-400 text-sm">
                                    {language === 'fr'
                                        ? 'Validez toutes les questions avant d\'obtenir les bonnes réponses et les explications.'
                                        : 'Validate all the questions before receiving the correct answers and explanations.'}
                                </p>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ErrorExamSection;
